import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Download, Star, TrendingUp, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const ResultsPage = () => {
  const { assessmentId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { token } = useAuth();

  const fetchReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assessments/${assessmentId}/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.text('ISO 27001:2022 Maturity Assessment Report', 20, 20);
    
    // Organization Info
    doc.setFontSize(12);
    doc.text(`Organization: ${report.organization?.org_name || 'N/A'}`, 20, 35);
    doc.text(`Assessment Date: ${new Date(report.completed_at).toLocaleDateString()}`, 20, 42);
    
    // Overall Score
    doc.setFontSize(16);
    doc.text('Overall Maturity Score', 20, 55);
    doc.setFontSize(24);
    doc.text(`${report.maturity_report?.overall?.star_rating || 0}/5 Stars`, 20, 65);
    doc.setFontSize(12);
    doc.text(`Maturity Level: ${report.maturity_report?.overall?.maturity_level || 'N/A'}`, 20, 73);
    
    // Category Scores Table
    const tableData = Object.entries(report.maturity_report || {})
      .filter(([key]) => key !== 'overall')
      .map(([category, data]) => [
        category,
        `${data.star_rating}/5`,
        data.maturity_level,
        `${data.average_score.toFixed(2)}`
      ]);
    
    doc.autoTable({
      startY: 85,
      head: [['Category', 'Rating', 'Maturity Level', 'Average Score']],
      body: tableData,
    });
    
    // Recommendations
    if (report.recommendations && report.recommendations.length > 0) {
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.text('Recommendations', 20, finalY);
      doc.setFontSize(10);
      report.recommendations.forEach((rec, index) => {
        doc.text(`${index + 1}. ${rec}`, 20, finalY + 8 + (index * 7), { maxWidth: 170 });
      });
    }
    
    doc.save(`ISO27001-Assessment-${assessmentId}.pdf`);
    toast.success('PDF downloaded successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Report not found</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  // Prepare radar chart data
  const radarData = Object.entries(report.maturity_report || {})
    .filter(([key]) => key !== 'overall')
    .map(([category, data]) => ({
      category: category,
      score: data.average_score
    }));

  // Prepare bar chart data
  const barData = Object.entries(report.maturity_report || {})
    .filter(([key]) => key !== 'overall')
    .map(([category, data]) => ({
      name: category,
      score: data.average_score
    }));

  return (
    <div className="bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <Shield className="h-8 w-8 text-slate-900" />
                <div>
                  <h1 className="font-heading font-bold text-2xl text-slate-900">Assessment Report</h1>
                  <p className="font-body text-sm text-slate-500">{report.organization?.org_name}</p>
                </div>
              </div>
            </div>
            <Button onClick={exportToPDF} data-testid="export-pdf-button">
              <Download className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Overall Score Card */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white rounded-lg p-8 mb-8 shadow-xl">
            <div className="text-center">
              <div className="font-mono text-xs uppercase tracking-widest mb-2 text-blue-200">Overall Maturity Score</div>
              <div className="flex items-center justify-center space-x-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-12 w-12 ${
                      i < (report.maturity_report?.overall?.star_rating || 0)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-slate-600'
                    }`}
                  />
                ))}
              </div>
              <div className="font-heading font-bold text-5xl mb-2">
                {report.maturity_report?.overall?.star_rating || 0}/5
              </div>
              <div className="font-body text-xl text-blue-100">
                {report.maturity_report?.overall?.maturity_level || 'N/A'} Level
              </div>
              <div className="mt-4 font-body text-sm text-slate-300">
                Assessed {report.maturity_report?.overall?.total_controls_assessed || 0} of 93 controls
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Radar Chart */}
            <div className="bg-white rounded-lg shadow-lg p-6 border border-slate-200">
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-6">Maturity Radar</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#cbd5e1" />
                  <PolarAngleAxis dataKey="category" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Radar name="Maturity Score" dataKey="score" stroke="#1e293b" fill="#1e293b" fillOpacity={0.6} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Bar Chart */}
            <div className="bg-white rounded-lg shadow-lg p-6 border border-slate-200">
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-6">Category Scores</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} angle={-15} textAnchor="end" height={80} />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#1e293b" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {Object.entries(report.maturity_report || {})
              .filter(([key]) => key !== 'overall')
              .map(([category, data]) => (
                <div key={category} className="bg-white rounded-lg shadow-lg p-6 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold text-lg text-slate-900">{category}</h3>
                    <div className="flex items-center">
                      {[...Array(data.star_rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-body text-sm text-slate-600">Average Score:</span>
                      <span className="font-heading font-semibold text-slate-900">{data.average_score.toFixed(2)}/5</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-body text-sm text-slate-600">Maturity Level:</span>
                      <span className="font-mono text-xs uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        {data.maturity_level}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-body text-sm text-slate-600">Controls Assessed:</span>
                      <span className="font-body text-sm text-slate-900">{data.total_controls}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Recommendations */}
          {report.recommendations && report.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-6">
                <TrendingUp className="h-6 w-6 text-blue-600" />
                <h3 className="font-heading font-semibold text-xl text-slate-900">Recommendations</h3>
              </div>
              <ul className="space-y-3">
                {report.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </span>
                    <span className="font-body text-slate-700 leading-relaxed">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsPage;